<template>
  <el-drawer
    :title="$t('nodeDetails.title')"
    :visible.sync="drawer"
    direction="rtl"
    size=900px
  >
    <div class="base-info__container">
      <div class="base-info__left">
        <div class="base-flex__cross-center base-margin__bottom-20">
          <h3>{{ $t('nodeDetails.instanceDetails') }}</h3>
        </div>
        <div class="tabs_box">
          <span>{{ $t('nodeDetails.tags') }}：</span>
          <el-tag
            v-for="tag in dynamicTags"
            :key="tag"
            :disable-transitions="false"
            closable
            @close="handleClose(tag)">
            {{ tag }}
          </el-tag>
          <el-input
            v-if="inputVisible"
            ref="saveTagInput"
            v-model="inputValue"
            class="input-new-tag"
            maxlength="45"
            @blur="handleInputConfirm"
            @keyup.enter.native="handleInputConfirm"
          >
          </el-input>
          <el-button v-if="!inputVisible && dynamicTags.length === 0" class="button-new-tag"
                     @click="showInput">{{ $t('nodeDetails.addTag') }}
          </el-button>
        </div>
        <div class="instanceDetailsCls">
          <div class="base-margin__bottom-20"><span>ID</span><span>{{ instanceDetails.id || '-' }}</span></div>
          <div class="base-margin__bottom-20">
            <span>{{ $t('nodeDetails.instanceStatus') }}</span>
            <span
              :style='`color:${filterInstanceState(instanceDetails.instanceState).color || "#D12734"};`'
            >{{
                filterInstanceState(instanceDetails.instanceState)?.label || '-'
              }}</span>
          </div>
          <div class="base-margin__bottom-20">
            <span>{{ $t('nodeDetails.instanceId') }}</span>
            <span
              class="base-color__light_blue base-cursor__pointer"
              @click="open('instance', instanceDetails.instanceId)">
                {{ instanceDetails.instanceId || '-' }}
            </span>
          </div>
          <div class="base-margin__bottom-20"><span>vCPU</span><span>{{
              instanceDetails.cpu || '-'
            }}{{ $t('nodeDetails.core') }}</span></div>
          <div class="base-margin__bottom-20"><span>{{ $t('nodeDetails.ipAddress') }}</span><span>{{
              instanceDetails.instancePublicIp || '-'
            }} / {{
              instanceDetails.instancePrivateIp || '-'
            }}</span>
          </div>
          <div class="base-margin__bottom-20"><span>vGPU</span><span>{{
              instanceDetails.gpu || '-'
            }}{{ $t('nodeDetails.card') }}</span></div>
          <div class="base-margin__bottom-20">
            <span>{{ $t('nodeDetails.imageId') }}</span>
            <span
              class="base-color__light_blue base-cursor__pointer"
              @click="open('img', instanceDetails.imageId)">
                {{ instanceDetails.imageId || '-' }}
            </span>
          </div>
          <div class="base-margin__bottom-20"><span>{{
              $t('nodeDetails.imageType')
            }}</span><span>{{
              instanceDetails.osName || '-'
            }} ( {{
              instanceDetails.imageType === 'SHARED_IMAGE' ? $t('createNode.sharedImages') : $t('createNode.publicImages')
            }} )</span>
          </div>
          <div class="base-margin__bottom-20">
            <span>{{ $t('nodeDetails.region') }}</span><span>{{
              regionNameMap.get(instanceDetails.region) || '-'
            }}</span></div>
          <div class="base-margin__bottom-20"><span>{{ $t('nodeDetails.zone') }}</span><span>{{
              zoneName() || '-'
            }}</span></div>
          <div class="base-margin__bottom-20">
            <span>{{
                $t('nodeDetails.billingMethod')
              }}</span><span>{{ filterInstanceChargeType(instanceDetails.instanceChargeType) || '-' }}</span>
          </div>
          <div class="base-margin__bottom-20"><span>{{
              $t('nodeDetails.instanceType')
            }}</span><span>{{ instanceDetails.instanceType || '-' }}</span>
          </div>
          <div class="base-margin__bottom-20">
            <span>{{ $t('nodeDetails.diskType') }}</span><span>{{
              filterDiskType(instanceDetails.diskType) || '-'
            }}</span></div>
          <div class="base-margin__bottom-20"><span>{{
              $t('nodeDetails.diskSize')
            }}</span><span>{{ instanceDetails.diskSize || '-' }} GB</span>
          </div>
          <div class="base-margin__bottom-20">
            <span>{{ $t('nodeDetails.networkBandwidth') }}</span><span>{{
              instanceDetails.internetMaxBandwidthOut || '-'
            }} GB</span></div>
          <div class="base-margin__bottom-20"><span>{{
              $t('nodeDetails.memory')
            }}</span><span>{{ instanceDetails.memory || '-' }} GB</span></div>
          <div class="base-margin__bottom-20">
            <span>{{ $t('nodeDetails.nodeType') }}</span><span>{{
              instanceDetails.nodeType === 'master' ? $t('nodeDetails.masterNode') :
                (instanceDetails.nodeType === 'login' ? $t('nodeDetails.loginNode') : $t('nodeDetails.workerNode'))
            }}</span>
          </div>
          <div class="base-margin__bottom-20">
            <span>{{ $t('nodeDetails.hyperThreading') }}</span><span>{{
              instanceDetails.hyperThreading ? $t('nodeDetails.enabled') : $t('nodeDetails.disabled')
            }}</span>
          </div>
          <div class="base-margin__bottom-20">
            <span>{{ $t('nodeDetails.associatedSubnet') }}</span>
            <span
              class="base-color__light_blue base-cursor__pointer"
              @click="open('subnet', instanceDetails?.subnetId)"
            >{{
                instanceDetails?.subnetId || '-'
              }}</span>
          </div>
        </div>
      </div>
      <div class="base-info__right base-margin__top-24">
        <h3 class="base-margin__bottom-20">{{ $t('nodeDetails.clusterDetails') }}</h3>
        <div class="envDetailsCls">
          <div class="base-margin__bottom-20"><span>{{
              $t('nodeDetails.envId')
            }}</span><span>{{ envDetails.env?.id || '-' }}</span></div>
          <div class="base-margin__bottom-20"><span>{{
              $t('nodeDetails.admin')
            }}</span><span>{{ envDetails?.user?.name || '_' }}</span></div>
          <div class="base-margin__bottom-20">
            <span>{{ $t('nodeDetails.associatedNetwork') }}</span>
            <span
              class="base-color__light_blue base-cursor__pointer"
              @click="open('vpc', envDetails.env?.vpcId)">
              {{ envDetails.env?.vpcId || '-' }}
            </span>
          </div>
          <div class="base-margin__bottom-20">
            <span>{{ $t('nodeDetails.associatedSubnet') }}</span>
            <span
              class="base-color__light_blue base-cursor__pointer"
              @click="open('subnet', envDetails.env?.subnetId)">
              {{ envDetails.env?.subnetId || '-' }}
            </span>
          </div>
          <div class="base-margin__bottom-20">
            <span>{{ $t('nodeDetails.cfsStorage') }}</span>
            <span
              class="base-color__light_blue base-cursor__pointer"
              @click="open('cfs', envDetails.env?.cfsId)">
              {{ envDetails.env?.cfsId || '-' }} ({{ envDetails.env?.cfsLink || '-' }})
            </span>
          </div>
          <div class="base-margin__bottom-20">
            <span>{{ $t('nodeDetails.cfsSecurityGroup') }}</span>
            <span class="base-color__light_blue base-cursor__pointer"
                  @click="open('cfsPermission', envDetails.env.cfsGroupId)">
              {{ envDetails.env?.cfsGroupId || '-' }}
            </span>
          </div>
          <div class="base-margin__bottom-20">
            <span>{{ $t('nodeDetails.cfsType') }}</span>
            <span>
              {{ filterCfsStorageTypeList(envDetails.env?.cfsStorageType) || '-' }}
            </span>
          </div>
          <div class="base-margin__bottom-20">
            <span>{{ $t('nodeDetails.cosStorage') }}</span>
            <span
              class="base-color__light_blue base-cursor__pointer"
              @click="open('cos', envDetails.env?.cosBucket)">
              {{ envDetails.env?.cosBucket || '-' }}
            </span>
          </div>
          <div class="base-margin__bottom-20">
            <span>{{ $t('nodeDetails.securityGroup') }}</span>
            <span class="base-color__light_blue base-cursor__pointer"
                  @click="open('sg', envDetails.env.securityGroupId)">
              {{ envDetails.env?.securityGroupId || '-' }}
            </span>
          </div>
          <div v-if="instanceDetails.isWindows" class="base-margin__bottom-20"><span>{{
              $t('nodeDetails.scheduler')
            }}</span><span>{{
              filterWorkloadManager(envDetails.env?.workloadManager) || '-'
            }}</span></div>
        </div>
      </div>
    </div>
  </el-drawer>
</template>

<script>
import {CvmStatusFilter, regionMap, regionNameMap} from '@/common/constants';
import {
  filterCfsStorageTypeList,
  filterDiskType,
  filterInstanceChargeType,
  filterInstanceState,
  filterWorkloadManager,
} from '@/common/utils/utils';
import {postRequest} from '@/common/libs/request';

export default {
  name: 'nodeDetails',
  computed: {},
  props: {},
  data() {
    return {
      drawer: false,
      titleName: this.$t('nodeDetails.title'),
      envDetails: {},
      instanceDetails: {},
      regionNameMap,
      filterDiskType,
      filterInstanceChargeType,
      filterInstanceState,
      filterWorkloadManager,
      filterCfsStorageTypeList,
      cvmStatusFilter: CvmStatusFilter,
      dynamicTags: [],
      inputVisible: false,
      inputValue: '',
      restartVNCLoading: false,
    };
  },

  methods: {
    mounted() {
      this.getSubclusterDetail();
    },

    restartVNC() {
      this.restartVNCLoading = true;
      this.$notify({
        title: this.$t('nodeDetails.restartVNC'),
        message: this.$t('nodeDetails.vncRestarting', {instanceId: this.instanceDetails.instanceId}),
        duration: 0,
        type: 'warning',
      });
      postRequest('/api/eda/common/project/reStartUserVNC', {
        instanceId: this.instanceDetails.instanceId,
      }).then(() => {
        this.restartVNCLoading = false;
        this.$notify({
          title: this.$t('nodeDetails.restartVNC'),
          message: this.$t('nodeDetails.vncRestartSuccess', {instanceId: this.instanceDetails.instanceId}),
          duration: 0,
          type: 'success',
        });
      })
        .catch(() => {
          this.restartVNCLoading = false;
          this.$message.error(this.$t('nodeDetails.vncRestartFailed'));
        });
    },

    handleClose(tag) {
      this.dynamicTags.splice(this.dynamicTags.indexOf(tag), 1);
      this.updateInstanceTag();
    },

    showInput() {
      this.inputVisible = true;
      this.$nextTick(() => {
        this.$refs.saveTagInput.$refs.input.focus();
      });
    },

    handleInputConfirm() {
      const {inputValue} = this;
      if (inputValue) {
        this.dynamicTags.push(inputValue);
      }
      this.updateInstanceTag();
      this.$parent.upDateTags(inputValue, this.instanceDetails.instanceId);
      this.inputVisible = false;
      this.inputValue = '';
    },

    updateInstanceTag() {
      postRequest('/api/eda/common/project/updateInstanceTag', {
        instanceId: this.instanceDetails.instanceId,
        name: this.dynamicTags,
      });
    },

    openNodeDetails(data) {
      const item = JSON.parse(window.sessionStorage.getItem('currentProject'));
      this.titleName = data.nodeType === 'master'
        ? this.$t('nodeDetails.masterNode')
        : this.$t('nodeDetails.workerNode');
      data.isWindows = data.osName.toLowerCase().indexOf('windows') === -1;
      this.instanceDetails = data;
      this.dynamicTags = data.tags || [];
      this.envDetails = item;
      this.drawer = true;
    },

    zoneName() {
      if (this.instanceDetails?.zone && this.instanceDetails?.region) {
        const zoneList = this.instanceDetails?.zone.split('-');
        return `${this.regionNameMap.get(this.instanceDetails?.region)}${zoneList[zoneList.length - 1]}${this.$t('nodeDetails.zoneSuffix')}`;
      }
      return '-';
    },

    open(type, val) {
      if (val === '') return false;
      switch (type) {
        case 'vpc':
          window.open(`https://console.cloud.tencent.com/vpc/vpc/detail?rid=${regionMap.get(this.instanceDetails.region)}&id=${val}`);
          break;
        case 'subnet':
          window.open(`https://console.cloud.tencent.com/vpc/subnet/detail?rid=${regionMap.get(this.instanceDetails.region)}&id=${val}`);
          break;
        case 'sg':
          window.open(`https://console.cloud.tencent.com/vpc/security-group/detail/${val}?rid=${regionMap.get(this.instanceDetails.region)}`);
          break;
        case 'img':
          window.open(`https://console.cloud.tencent.com/cvm/image/detail?rid=${regionMap.get(this.instanceDetails.region)}&id=${val}`);
          break;
        case 'cfs':
          window.open(`https://console.cloud.tencent.com/cfs/fs/detail?rid=${regionMap.get(this.instanceDetails.region)}&id=${val}`);
          break;
        case 'cfsPermission':
          window.open(`https://console.cloud.tencent.com/cfs/permission/detail?rid=${regionMap.get(this.instanceDetails.region)}&id=${val}`);
          break;
        case 'cos':
          window.open(`https://console.cloud.tencent.com/cos/bucket?bucket=${val}&region=${this.instanceDetails.region}`);
          break;
        case 'instance':
          window.open(`https://console.cloud.tencent.com/cvm/instance/detail?searchParams=rid%3D33&rid=${regionMap.get(this.instanceDetails.region)}&id=${val}`);
          break;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.previewDialog_main--div {
  max-height: 500px;
  overflow: auto;
}

.base-info__container {
  height: 100%;
  background: #fff;
  padding: 10px 20px;

  span:first-child {
    display: inline-block;
    width: 130px;
    margin-right: 20px;
  }
}

.tabs_box {
  margin-bottom: 20px;
  display: flex;
  align-items: center;

  span:first-child {
    display: inline-block;
    width: 130px;
    margin-right: 0px;
    font-size: 14px;
  }

  span {
    //margin-bottom: 10px;
  }
}

//.base-info__left, .base-info__right {
//  h3 {
//    margin-bottom: 30px;
//  }
//}

.envDetailsCls, .instanceDetailsCls {
  display: flex;
  flex-wrap: wrap;
  font-size: 14px;

  div {
    width: 50%;
  }
}

.status__color-create {
  color: #165BCF;
}

.status__color-Normal {
  color: #0BC740;
}

.status__color-InDestruction, .status__color-Destruction, .status__color-Abnormal {
  color: #D12734
}

.el-tag + .el-tag {
  margin-left: 10px;
}

.button-new-tag {
  margin-left: 10px;
  height: 32px;
  line-height: 30px;
  padding-top: 0;
  padding-bottom: 0;
}

.input-new-tag {
  width: 120px;
  margin-left: 10px;
  vertical-align: top;
}
</style>
